import { Component, inject, OnInit, signal, computed, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ImpresionService } from '../../services/impresion.service';
import { TEXTO_MOTIVO } from '../../services/impresion.service';
import { FeatureDirective } from '../../core/tenant/feature.directive';
import { SessionService } from '../../core/tenant/session.service';
import { PagerConfigService } from '../../services/pager-config.service';
import { pesos } from '../../core/ui/pesos';
import { TableService } from '../../services/table.service';
import { WaiterAdminService, WaiterAdmin } from '../../services/waiter-admin.service';
import { AuthService, JWT_STORAGE_KEY } from '../../core/tenant/auth.service';
import { ExpenseService, OpcionDeGasto } from '../../services/expense.service';
import { htmlDeListas, terceroElegido } from './gasto-de-caja';
import { HttpClient } from '@angular/common/http';
import { ConsumoPropioService } from '../../services/consumo-propio.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { puedePedirAProveedorDelToken } from '../../core/tenant/negocio-del-token';
import { MenuService } from '../../services/menu.service';
import { PerfilService } from '../../core/perfil/perfil.service';
import Swal from 'sweetalert2';
import { motivoDelError } from '../../core/ui/motivo-del-error';
import { cambiosDelNegocio } from '../../core/tenant/datos-del-negocio';
import { VendedoresService } from '../../services/vendedores.service';
import { OfflineOrderService } from '../../core/offline/offline-order.service';
import { FeatureService } from '../../core/tenant/feature.service';
import { activarCampoDeDinero, leerPesos } from '../../core/ui/campo-de-dinero.directive';
import { VersionServidaComponent } from '../../core/version/version-servida.component';

import { DemoService } from '../../core/demo/demo.service';
import { AvisoDelDemoComponent } from '../../core/demo/aviso-del-demo.component';
import { UsuariosDelNegocioComponent } from './usuarios-del-negocio.component';
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, FeatureDirective, VersionServidaComponent, UsuariosDelNegocioComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit {
  readonly impresion = inject(ImpresionService);
  private session = inject(SessionService);
  private auth = inject(AuthService);
  private waiterAdmin = inject(WaiterAdminService);
  private pagerConfig = inject(PagerConfigService);
  tableService = inject(TableService);
  private expenses = inject(ExpenseService);
  private http = inject(HttpClient);
  /** Z23: «Consumo propio» en el menú solo si -mt lo ofrece (personas 200). */
  readonly consumoPropio = inject(ConsumoPropioService);
  private menu = inject(MenuService);
  private router = inject(Router);
  private offlineOrders = inject(OfflineOrderService);
  private features = inject(FeatureService);
  private readonly usuariosDelNegocio = viewChild.required(UsuariosDelNegocioComponent);
  private demo = inject(DemoService);
  /** En el demo local (ver `core/demo/demo.ts`). Se lee al montar: la barra se monta al entrar a la caja. */
  readonly enDemo = this.demo.activo();
  readonly tenant = this.session.session;
  /** El perfil vertical del negocio (T1): su nombre y su acento en la cabecera. */
  readonly perfil = inject(PerfilService);
  private readonly vendedores = inject(VendedoresService);

  /**
   * Se elige vendedor si el negocio es mayorista y quien entró no ES el vendedor (entonces es él). No se mira si la
   * lista ya llegó: si la entrada dependiera de eso, desaparecería de la barra sin red o antes de abrir la caja.
   */
  readonly puedeElegirVendedor = computed(() => this.features.has('mayorista') && !this.vendedores.esVendedor());

  /** Lo que se lee en la barra: el elegido, o que no hay ninguno. */
  readonly vendedorElegido = computed(() => {
    const v = this.vendedores.elegido();
    return v ? VendedoresService.etiqueta(v, this.vendedores.vendedores()) : 'sin asignar';
  });

  /**
   * El selector, fuera de la venta (Santiago, 20/09: «el dropdown sería mejor en el sidebar… que una vez seleccionado
   * se mantenga hasta que se cambie»). Lo abre también el nombre de la franja fija de la caja.
   */
  elegirVendedor(): Promise<void> {
    return this.vendedores.abrirSelector();
  }

  readonly isAdmin = this.session.isAdmin; // gating por rol (F3)

  /**
   * T7.3 · «Pedir a mi proveedor» (ECM, opción c, 25/09): módulo `pedidos_proveedores`, admin O el claim de -mt V126,
   * y al menos una relación ACTIVA. Abre «Mis proveedores» del panel en pestaña nueva. Hoy solo lo alcanza el admin:
   * el panel no deja entrar a nadie más hasta que Santiago decida abrirlo.
   */
  panelUrl = environment.panelUrl || null;
  readonly atajoProveedor = signal(false);
  get urlMisProveedores(): string {
    return `${this.panelUrl}/panel/comprador/proveedores`;
  }
  private async cargarAtajoProveedor(): Promise<void> {
    let token: string | null = null;
    try { token = localStorage.getItem(JWT_STORAGE_KEY); } catch { /* sin almacenamiento */ }
    if (!this.panelUrl || !this.features.has('pedidos_proveedores') || !(this.isAdmin() || puedePedirAProveedorDelToken(token))) return;
    try {
      const relaciones = await firstValueFrom(this.http.get<{ estado: string }[]>(`${environment.apiBaseUrl}/api/red/comprador/proveedores`));
      this.atajoProveedor.set(relaciones.some((r) => r.estado === 'ACTIVA'));
    } catch { /* sin la lista, sin atajo */ }
  }
  /**
   * Indicador del agente con nombre honesto (docs/arquitectura/21 §2.4):
   * verde solo si el agente afirma `lista`; ámbar «agente conectado» cuando
   * responde pero no puede afirmar más; rojo cuando no responde. Nada si la
   * sede no imprime, ni con la vía navegador (diseño 22: no hay agente que vigilar).
   */
  readonly indicadorImpresion = computed<{ nivel: 'ok' | 'aviso' | 'error'; texto: string } | null>(() => {
    if (!this.impresion.activa()) return null;
    if (this.impresion.viaActual() === 'navegador') return null;
    const e = this.impresion.estadoAgente();
    if (!e) return { nivel: 'aviso', texto: 'Buscando el programa de impresión…' };
    if (!e.conectado) {
      return {
        nivel: 'error',
        texto: e.motivo === 'navegador_pide_permiso' ? 'Chrome pide permiso' : 'Sin programa de impresión',
      };
    }
    switch (e.impresora) {
      case 'lista': return { nivel: 'ok', texto: 'Impresora lista' };
      case 'no_encontrada': return { nivel: 'aviso', texto: 'Agente conectado · sin impresora' };
      case 'sin_conexion': return { nivel: 'aviso', texto: 'Agente conectado · impresora sin conexión' };
      case 'en_cola': return { nivel: 'aviso', texto: `Agente conectado · ${e.enCola ?? ''} en cola`.replace('  ', ' ') };
      case 'error': return { nivel: 'aviso', texto: `Agente conectado · ${e.detalle || 'error de impresora'}` };
      default: return { nivel: 'aviso', texto: 'Agente conectado · impresora sin datos' };
    }
  });
  probandoImpresion = signal(false);

  /** Drawer móvil (≤900px): abierto/cerrado. En desktop el CSS lo ignora. */
  mobileOpen = signal<boolean>(false);

  toggleMobile(): void {
    this.mobileOpen.update((v) => !v);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  /**
   * Gestión de usuarios (F3, solo admin): listar + crear cajero/admin.
   * F1.10 (mayoristas): con el módulo `mayorista` también se crea el rol
   * Vendedor, con su nombre visible (es lo que sale en el selector de la caja).
   */
  async manageUsers() {
    await this.usuariosDelNegocio().abrir();
  }


  /**
   * Administración de meseros (F5, solo admin): listar, crear y editar (nombre,
   * meta diaria, base por defecto, activo/inactivo). Reemplaza el alta por API.
   */
  /**
   * N2/6.7 — configuración de rastreadores. Se edita el NOMBRE, el COLOR y la
   * CANTIDAD de cada grupo. El código del grupo NO se toca: es lo que queda
   * escrito en las órdenes y renombrarlo dejaría el historial sin referencia.
   */
  /**
   * N3 — Cantidad de mesas. Las sobrantes se DESACTIVAN en el backend, no se
   * borran: una mesa borrada dejaría huérfanas sus cuentas históricas.
   */
  async configureTables() {
    await this.tableService.cargarMesas();
    const actuales = this.tableService.mesas().length;

    const { value } = await Swal.fire({
      title: 'Mesas del negocio',
      input: 'number',
      inputLabel: '¿Cuántas mesas tiene el local?',
      inputValue: String(actuales),
      inputAttributes: { min: '0', max: '500' },
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => {
        const n = Number(v);
        return !(n >= 0 && n <= 500) ? 'Debe estar entre 0 y 500' : undefined;
      },
    });
    if (value === undefined || value === null || value === '') return;

    try {
      await this.tableService.configurarCantidad(Number(value));
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: Number(value) === 1 ? '1 mesa configurada' : `${value} mesas configuradas` });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar',
        text: e?.error?.error ?? 'Intenta de nuevo.' });
    }
  }

  async configurePagers() {
    await this.pagerConfig.load();
    // Sin grupos, los dos de siempre como PLANTILLA en 0: el administrador pone los que tenga (18/09).
    const grupos = this.pagerConfig.groups().length ? this.pagerConfig.groups() : PagerConfigService.PLANTILLA;
    const filas = grupos.map((g, i) => `
      <div style="display:flex;gap:.4rem;align-items:center;margin-bottom:.5rem">
        <input id="pg-label-${i}" class="swal2-input" style="margin:0;flex:1;min-width:0"
               value="${g.label}" placeholder="Nombre">
        <input id="pg-color-${i}" type="color" value="${g.color}"
               style="width:2.5rem;height:2.5rem;border:none;background:none;padding:0">
        <input id="pg-qty-${i}" class="swal2-input" style="margin:0;width:5.5rem" type="number"
               min="0" max="200" value="${g.quantity}" placeholder="Cant.">
      </div>`).join('');

    const { isConfirmed } = await Swal.fire({
      title: 'Rastreadores',
      html: `<p style="font-size:.82rem;color:#64748b;margin:0 0 .75rem">
               Nombre, color y cantidad por grupo. El historial de órdenes no se ve afectado.
             </p>${filas}`,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      focusConfirm: false,
      preConfirm: async () => {
        const val = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value ?? '';
        const payload = grupos.map((g, i) => ({
          code: g.code,
          label: val(`pg-label-${i}`).trim(),
          color: val(`pg-color-${i}`),
          quantity: Number(val(`pg-qty-${i}`)),
        }));
        if (payload.some(p => !p.label)) {
          Swal.showValidationMessage('El nombre del grupo no puede quedar vacío');
          return undefined;
        }
        if (payload.some(p => !(p.quantity >= 0 && p.quantity <= 200))) {
          Swal.showValidationMessage('La cantidad debe estar entre 0 y 200');
          return undefined;
        }
        try {
          await this.pagerConfig.save(payload);
          return true;
        } catch (e: any) {
          Swal.showValidationMessage(e?.error?.error ?? 'No se pudo guardar la configuración');
          return undefined;
        }
      },
    });

    if (isConfirmed) {
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Rastreadores actualizados' });
    }
  }

  async manageWaiters() {
    let waiters: WaiterAdmin[] = [];
    try {
      waiters = await this.waiterAdmin.getAll();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cargar', text: e instanceof Error ? e.message : '' });
      return;
    }
    const esc = (v: string) => (v ?? '').replace(/[<>&]/g, '');
    // F1.14: el oficio lo dice el perfil («mesero» en restaurante). Sin artículos ni participios junto al término.
    const vendedor = this.perfil.termino('vendedor');
    // F1.16: `pesos()` ya distingue «no hay dato» (—) de «cero» ($ 0); el ayudante de aquí lo hacía a medias.
    const money = (n: number | null) => pesos(n);
    // A9: la primera clave del mesero la pone el admin, aquí; también la cambia sin saber la actual.
    const botonDeClave = (w: WaiterAdmin) => w.tienePin === undefined ? ''
      : ` <button type="button" class="w-clave" data-id="${Number(w.id)}" style="margin-left:.4rem;font-size:.75rem;padding:.1rem .45rem;border:1px solid #cbd5e1;border-radius:.4rem;background:#fff;cursor:pointer">${w.tienePin ? 'Cambiar clave' : 'Poner clave'}</button>`;
    const list = waiters.length
      ? waiters.map((w) =>
          `• ${esc(w.name)} ${w.active ? '' : '<i>(inactivo)</i>'} — meta ${money(w.dailySaleGoal)} · base ${money(w.defaultCashBase)}${botonDeClave(w)}`)
        .join('<br>')
      : `(Aún no hay ${this.perfil.termino('vendedor', 'p')})`;

    const result = await Swal.fire({
      title: this.perfil.termino('vendedor', 'pt'),
      html:
        `<div style="text-align:left;font-size:.82rem;margin:0 0 .75rem">${list}</div>` +
        `<input id="w-name" class="swal2-input" placeholder="Nombre">` +
        `<input id="w-goal" type="text" inputmode="numeric" class="swal2-input" placeholder="Meta diaria (opcional)">` +
        `<input id="w-base" type="text" inputmode="numeric" class="swal2-input" placeholder="Base de caja por defecto (opcional)">`,
      focusConfirm: false,
      didOpen: () => {
        ['w-goal', 'w-base'].forEach((id) => activarCampoDeDinero(document.getElementById(id) as HTMLInputElement));
        document.querySelectorAll<HTMLButtonElement>('.w-clave').forEach((b) => b.addEventListener('click', () => {
          const w = waiters.find((x) => x.id === Number(b.dataset['id']));
          if (w) void this.ponerClave(w); // Swal no apila: este diálogo reemplaza al de la lista
        }));
      },
      showCancelButton: true,
      showDenyButton: waiters.length > 0,
      confirmButtonText: `Crear ${vendedor}`,
      denyButtonText: `Editar ${vendedor}`,
      cancelButtonText: 'Cerrar',
      preConfirm: () => {
        const val = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value.trim() ?? '';
        const name = val('w-name');
        if (!name) { Swal.showValidationMessage('El nombre es obligatorio'); return undefined; }
        const goal = val('w-goal'), base = val('w-base');
        return { name, goal: leerPesos(goal), base: leerPesos(base) };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        await this.waiterAdmin.create(result.value.name, result.value.goal, result.value.base);
        Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
          icon: 'success', title: 'Guardado' });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'No se pudo crear', text: e instanceof Error ? e.message : '' });
      }
      return;
    }

    if (result.isDenied) {
      await this.editWaiter(waiters);
    }
  }

  /** A9: «Clave de {nombre}», 4 dígitos, sin pedir la actual (textos de Producto, §A9 P1). */
  async ponerClave(w: WaiterAdmin): Promise<void> {
    const { value: pin } = await Swal.fire({
      title: `Clave de ${w.name}`,
      input: 'password',
      inputPlaceholder: 'Clave de 4 dígitos',
      inputAttributes: { inputmode: 'numeric', maxlength: '4', autocomplete: 'off' },
      text: 'El mesero la usa para entrar en su app. Después la puede cambiar él mismo.',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => /^\d{4}$/.test((v ?? '').trim()) ? null : 'Clave de 4 dígitos',
    });
    if (!pin) return;
    try {
      await this.waiterAdmin.ponerClave(w.id, String(pin).trim());
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Clave guardada.' });
    } catch (e) {
      const cuerpo = (e as { error?: { mensaje?: string; message?: string } })?.error;
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: cuerpo?.mensaje ?? cuerpo?.message ?? '' });
    }
  }

  private async editWaiter(waiters: WaiterAdmin[]) {
    const esc = (v: string) => (v ?? '').replace(/[<>&"]/g, '');
    const options = waiters
      .map((w) => `<option value="${w.id}">${esc(w.name)}${w.active ? '' : ' (inactivo)'}</option>`)
      .join('');
    const { value } = await Swal.fire({
      title: `Editar ${this.perfil.termino('vendedor')}`,
      html:
        `<select id="we-id" class="swal2-input">${options}</select>` +
        `<input id="we-name" class="swal2-input" placeholder="Nuevo nombre (vacío = sin cambio)">` +
        `<input id="we-goal" type="text" inputmode="numeric" class="swal2-input" placeholder="Meta diaria (vacío = sin cambio)">` +
        `<input id="we-base" type="text" inputmode="numeric" class="swal2-input" placeholder="Base por defecto (vacío = sin cambio)">` +
        `<label style="font-size:.85rem;display:flex;gap:.4rem;justify-content:center;align-items:center;margin-top:.5rem">` +
        `<input id="we-active" type="checkbox" checked> Activo</label>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar cambios',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        ['we-goal', 'we-base'].forEach((id) => activarCampoDeDinero(document.getElementById(id) as HTMLInputElement));
        const sel = document.getElementById('we-id') as HTMLSelectElement;
        const sync = () => {
          const w = waiters.find((x) => x.id === Number(sel.value));
          if (!w) return;
          (document.getElementById('we-active') as HTMLInputElement).checked = w.active;
        };
        sel.addEventListener('change', sync);
        sync();
      },
      preConfirm: () => {
        const val = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value.trim() ?? '';
        const id = Number(val('we-id'));
        const name = val('we-name'), goal = val('we-goal'), base = val('we-base');
        const active = (document.getElementById('we-active') as HTMLInputElement).checked;
        return {
          id,
          patch: {
            ...(name ? { name } : {}),
            active,
            ...(leerPesos(goal) !== null ? { dailySaleGoal: leerPesos(goal) } : {}),
            ...(leerPesos(base) !== null ? { defaultCashBase: leerPesos(base) } : {}),
          },
        };
      },
    });
    if (!value) return;
    try {
      await this.waiterAdmin.update(value.id, value.patch);
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Cambios guardados' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: e instanceof Error ? e.message : '' });
    }
  }

  /** Registrar un gasto de caja EN CUALQUIER momento del turno (F5, cluster caja). */
  async registerExpense() {
    // B9.5: las listas opcionales de core. Si no cargan (o tardan más de 3 s), no se pintan y se registra como siempre.
    const [categorias, proveedores] = (await Promise.allSettled([this.expenses.categorias(), this.expenses.proveedores()]))
      .map(r => (r.status === 'fulfilled' ? r.value : [] as OpcionDeGasto[]));
    const { value } = await Swal.fire({
      title: 'Registrar gasto de caja',
      html:
        `<input id="e-concept" class="swal2-input" placeholder="Concepto (ej: servilletas)">` +
        `<input id="e-amount" type="text" inputmode="numeric" class="swal2-input" placeholder="Monto ($)">` +
        htmlDeListas(categorias, proveedores),
      focusConfirm: false,
      didOpen: () => activarCampoDeDinero(document.getElementById('e-amount') as HTMLInputElement),
      showCancelButton: true,
      confirmButtonText: 'Registrar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const val = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value.trim() ?? '';
        const concept = val('e-concept');
        const amount = leerPesos(val('e-amount')) ?? 0;
        if (!concept) { Swal.showValidationMessage('El concepto es obligatorio'); return undefined; }
        if (!amount || amount <= 0) { Swal.showValidationMessage('Monto inválido'); return undefined; }
        const tercero = terceroElegido(id => (document.getElementById(id) as HTMLSelectElement | null)?.value ?? null);
        return { concept, amount, tercero };
      },
    });
    if (!value) return;
    try {
      await this.expenses.create(value.concept, value.amount, this.tenant()?.userName, undefined, value.tercero);
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Gasto registrado (se descuenta en el cierre)' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo registrar', text: motivoDelError(e) }); // un HttpErrorResponse no es Error: antes salía vacío
    }
  }

  /** Registrar una BAJA de comida (F5 A15): producto, cantidad y motivo. */
  async registerWaste() {
    const products = this.menu.categoriesWithProducts()
      .flatMap((c: any) => c.products ?? [])
      .map((p: any) => ({ id: p.idProduct ?? p.id, name: p.nameProduct ?? p.name }));
    const esc = (v: string) => (v ?? '').replace(/[<>&"]/g, '');
    const options = products.length
      ? products.map((p: any) => `<option value="${esc(p.id)}">${esc(p.name)}</option>`).join('')
      : '';
    const { value } = await Swal.fire({
      title: 'Registrar baja de comida',
      html:
        (options
          ? `<select id="fw-product" class="swal2-input">${options}</select>`
          : `<input id="fw-name" class="swal2-input" placeholder="Producto">`) +
        `<input id="fw-qty" type="number" min="0.5" step="0.5" class="swal2-input" placeholder="Cantidad" value="1">` +
        `<select id="fw-reason" class="swal2-input">` +
        `<option value="CAIDA">Se cayó</option><option value="DANADA">Dañada</option>` +
        `<option value="VENCIDA">Vencida</option><option value="ERROR_PREPARACION">Error de preparación</option>` +
        `<option value="OTRO">Otro</option></select>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Registrar baja',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const el = (id: string) => document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
        const qty = Number((el('fw-qty') as HTMLInputElement)?.value || 0);
        if (!qty || qty <= 0) { Swal.showValidationMessage('Cantidad inválida'); return undefined; }
        const reason = (el('fw-reason') as HTMLSelectElement).value;
        const sel = el('fw-product') as HTMLSelectElement | null;
        const productId = sel ? sel.value : null;
        const productName = sel
          ? sel.selectedOptions[0]?.textContent?.trim() ?? sel.value
          : (el('fw-name') as HTMLInputElement)?.value.trim();
        if (!productName) { Swal.showValidationMessage('El producto es obligatorio'); return undefined; }
        return { productId, productName, quantity: qty, reason };
      },
    });
    if (!value) return;
    try {
      await firstValueFrom(this.http.post(`${environment.apiBaseUrl}/api/food-waste`, {
        ...value, createdBy: this.tenant()?.userName,
      }));
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Baja registrada' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo registrar', text: motivoDelError(e) }); // un HttpErrorResponse no es Error: antes salía vacío
    }
  }

  /**
   * F2.2 — Cerrar sesión no borra las ventas que no se han enviado: quedan en
   * este equipo y solo salen con la cuenta de su negocio. Si hay alguna, se dice
   * antes de salir, para que nadie cierre creyendo que todo llegó.
   */
  async logout() {
    // En el demo, cerrar sesión es salir del demo: sin esto quedaba en el equipo lo que se hizo en él.
    if (this.enDemo) {
      await this.demo.salir();
      AvisoDelDemoComponent.irAlLogin();
      return;
    }
    const { pendientes, rechazadas } = await this.offlineOrders
      .contadores()
      .catch(() => ({ pendientes: 0, rechazadas: 0, deOtrosNegocios: 0 }));
    const sinEnviar = pendientes + rechazadas;
    if (sinEnviar > 0) {
      const una = sinEnviar === 1;
      const r = await Swal.fire({
        icon: 'warning',
        title: `${sinEnviar} venta${una ? '' : 's'} sin enviar`,
        text:
          `No se pierde${una ? '' : 'n'}: queda${una ? '' : 'n'} guardada${una ? '' : 's'} en este equipo ` +
          `y se envía${una ? '' : 'n'} cuando vuelvas a entrar con esta cuenta. Con otra cuenta no se envía${una ? '' : 'n'}.`,
        showCancelButton: true,
        confirmButtonText: 'Cerrar sesión igual',
        cancelButtonText: 'Quedarme',
        reverseButtons: true,
      });
      if (!r.isConfirmed) return;
    }
    this.session.logout();
    this.router.navigate(['/login']);
  }

  /** Ver/editar los datos del negocio que se imprimen en el ticket. */
  async editBusiness() {
    let current;
    try {
      current = await this.auth.getBusiness();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cargar',
        text: e instanceof Error ? e.message : 'Error inesperado' });
      return;
    }
    const esc = (v: string | null | undefined) => (v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    // M2 + M17 (textos de Producto): nombre y NIT de solo lectura (los cambia SureSell); lo demás sin clave.
    const { value } = await Swal.fire({
      title: 'Datos del negocio',
      html:
        `<div style="text-align:left;font-size:.85rem;margin:0 0 .5rem">` +
        `<div>Nombre del negocio: <b id="b-name">${esc(current.name)}</b></div>` +
        `<div>NIT: <b id="b-nit">${esc(current.nit) || '—'}</b></div>` +
        `<small style="color:#64748b">El nombre y el NIT los cambia SureSell. Si cambiaron, escríbenos.</small></div>` +
        `<input id="b-razon" class="swal2-input" placeholder="Razón social" value="${esc(current.razonSocial)}">` +
        `<input id="b-address" class="swal2-input" placeholder="Dirección" value="${esc(current.address)}">` +
        `<input id="b-phone" class="swal2-input" placeholder="Teléfono" value="${esc(current.phone)}">` +
        `<input id="b-correo" class="swal2-input" type="email" placeholder="Correo de contacto" value="${esc(current.correoContacto)}">` +
        // M34 A7: un dato, un sitio. El pie (ticket_footer) solo se edita en el panel (Producto, 52c8877).
        `<small id="b-pie" style="display:block;color:#64748b">El pie de la tirilla lo cambia el administrador en el panel, en «Ajustes de venta» › «Comprobante».</small>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const val = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value.trim() ?? '';
        // Solo lo cambiado (el servidor es parcial desde -mt 43a239a). Nunca viajan nombre, NIT ni clave.
        return cambiosDelNegocio(current, {
          razonSocial: val('b-razon'), address: val('b-address'), phone: val('b-phone'),
          correoContacto: val('b-correo'),
        });
      },
    });
    if (!value || Object.keys(value).length === 0) return;
    try {
      const saved = await this.auth.updateBusiness(value);
      this.session.updateBusinessProfile({
        tenantName: saved.name, nit: saved.nit, address: saved.address,
        phone: saved.phone, ticketFooter: saved.ticketFooter,
      });
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Datos actualizados' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar',
        text: e instanceof Error ? e.message : 'Error inesperado' });
    }
  }

  /** Cambio de contraseña del usuario logueado (prompt con clave actual + nueva). */
  async changePassword() {
    const { value } = await Swal.fire({
      title: 'Cambiar contraseña',
      html:
        '<input id="swal-cur" type="password" class="swal2-input" placeholder="Contraseña actual" autocomplete="current-password">' +
        '<input id="swal-new" type="password" class="swal2-input" placeholder="Nueva contraseña (mín. 6)" autocomplete="new-password">',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const cur = (document.getElementById('swal-cur') as HTMLInputElement)?.value ?? '';
        const nw = (document.getElementById('swal-new') as HTMLInputElement)?.value ?? '';
        if (!cur || !nw) {
          Swal.showValidationMessage('Completa ambos campos');
          return undefined;
        }
        if (nw.length < 6) {
          Swal.showValidationMessage('La nueva contraseña debe tener al menos 6 caracteres');
          return undefined;
        }
        return { cur, nw };
      },
    });
    if (!value) return;
    try {
      await this.auth.changePassword(value.cur, value.nw);
      Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 2500,
        icon: 'success', title: 'Contraseña actualizada' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar',
        text: e instanceof Error ? e.message : 'Error inesperado' });
    }
  }

  ngOnInit() {
    // N3 — El sidebar necesita el modo para decidir si muestra "Mesas", aunque
    // el usuario no haya entrado al POS todavía.
    // La lista de vendedores, para el selector de la barra: la caja también la pide, pero desde aquí se entra sin
    // pasar por ella (y este servicio guarda lo que ya sabía si no hay red).
    if (this.puedeElegirVendedor()) void this.vendedores.cargar();
    void this.cargarAtajoProveedor(); // una vez, sin sondeo
    // Z167: la entrada «Consumo propio» sale si -mt ofrece las personas. Solo las pedía la caja (/pos): entrando por la
    // URL o recargando, el enlace no salía. Solo la pregunta, no `cargarPersonas()`: esa además manda la cola, y aquí
    // puede estar un rol que no registra (DrDev002).
    void this.consumoPropio.pedirPersonas();
    this.tableService.cargarModo().then(() => {
      // Cada 30 s, al volver el foco y tras cada impresión; cero si la sede no imprime.
      this.impresion.iniciarSondeo();
      this.impresion.cargarPendientes();
    });
  }

  /** Clic humano: siempre intenta, aunque el sondeo automático se haya rendido. */
  async reintentarImpresion() {
    await this.impresion.consultarEstado();
  }

  /** 04/10: el ajuste solo se pinta si el agente vuelve a usarse. */
  get agenteEnUso(): boolean { return ImpresionService.AGENTE_EN_USO; }

  /** Diseño 22 §6 — el ajuste del equipo «Imprimir con el navegador». */
  cambiarVia(conNavegador: boolean) {
    this.impresion.elegirVia(conNavegador ? 'navegador' : 'agente');
    // De vuelta al agente: el indicador se busca ya, aunque el sondeo se hubiera rendido.
    if (!conNavegador) void this.reintentarImpresion();
  }

  /** Ticket corto de prueba, sin cajón. */
  async probarImpresion() {
    if (this.probandoImpresion()) return;
    this.probandoImpresion.set(true);
    try {
      const r = await this.impresion.probar();
      Swal.fire({
        toast: true, position: 'bottom-start', showConfirmButton: false, timer: 3500,
        icon: r.estado === 'no_impreso' ? 'error' : 'success',
        title: r.estado === 'no_impreso' ? 'No se imprimió' : 'Ticket de prueba enviado a la impresora',
        text: r.estado === 'no_impreso' ? (TEXTO_MOTIVO[r.motivo ?? 'error_agente'] + (r.detalle ? ` (${r.detalle})` : '')) : undefined,
      });
    } finally {
      this.probandoImpresion.set(false);
    }
  }

  async openCashDrawer() {
    const r = await this.impresion.abrirCajon();
    if (r.estado === 'no_impreso') {
      Swal.fire({
        toast: true,
        position: 'bottom-start',
        showConfirmButton: false,
        timer: 3000,
        title: 'No se pudo abrir el cajón',
        text: TEXTO_MOTIVO[r.motivo ?? 'error_agente'],
        icon: 'error'
      });
    }
  }
}
